import { getSupabase as getSupabaseNullable } from "@veltro/shared/lib/supabase";
import { logger } from "@/shared/utils/logger";
import type { DeviceInfo } from "@/shared/utils/deviceDetection";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ActiveSession {
  id: string;
  userId: string;
  tenantId: string;
  licenseType: string;
  deviceType: string;
  clientType: string;
  deviceFingerprint: string | null;
  deviceName: string | null;
  ipAddress: string | null;
  status: string;
  lastHeartbeat: string;
  createdAt: string;
  terminatedAt: string | null;
  terminatedReason: string | null;
}

export type SessionDeniedReason =
  | "operator_desktop_blocked"
  | "operator_web_blocked";

export interface SessionCheckResult {
  allowed: boolean;
  reason?: SessionDeniedReason;
  /** Name of the device that was auto-kicked (for toast notification) */
  kickedDeviceName?: string | null;
}

interface CreateSessionResult {
  sessionId: string;
  success: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_SESSIONS: Record<string, number> = {
  operator: 1,
  full_user: 2,
};

/** Sessions with heartbeats older than this are considered ghosts (1 hour — B2B users leave tabs open) */
const GHOST_THRESHOLD_SECONDS = 3600;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

function mapSession(row: Record<string, unknown>): ActiveSession {
  return {
    id: (row.session_id ?? row.id) as string,
    userId: row.user_id as string,
    tenantId: row.tenant_id as string,
    licenseType: row.license_type as string,
    deviceType: row.device_type as string,
    clientType: row.client_type as string,
    deviceFingerprint: (row.device_fingerprint as string) ?? null,
    deviceName: (row.device_name as string) ?? null,
    ipAddress: (row.ip_address as string) ?? null,
    status: row.status as string,
    lastHeartbeat: row.last_heartbeat as string,
    createdAt: row.created_at as string,
    terminatedAt: (row.terminated_at as string) ?? null,
    terminatedReason: (row.terminated_reason as string) ?? null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

export class SessionService {
  /**
   * Check whether a new session is allowed for this user + license + device.
   *
   * V2 rules:
   * - System admins bypass all session limits (unlimited sessions)
   * - Ghost sessions (stale heartbeat > 1 hour) are cleaned up first
   * - If at limit, auto-kick the oldest active session (no conflict modal)
   */
  static async checkSessionAccess(
    userId: string,
    licenseType: string,
    deviceInfo: DeviceInfo,
    isSystemAdmin: boolean,
  ): Promise<SessionCheckResult> {
    // Rule 1: System admins bypass all session checks
    if (isSystemAdmin) {
      return { allowed: true };
    }

    // Operator accounts: block desktop (mobile-only)
    if (licenseType === "operator" && deviceInfo.device_type === "desktop") {
      return { allowed: false, reason: "operator_desktop_blocked" };
    }

    try {
      // Rule 3: Clean up ghost sessions first
      await SessionService.cleanupGhostSessions(userId);

      // Count only fresh sessions (heartbeat within 90s)
      const freshSessions = await SessionService.getFreshActiveSessions(userId);
      const max = MAX_SESSIONS[licenseType] ?? 1;

      // Rule 2: Auto-kick oldest if at limit
      if (freshSessions.length >= max) {
        const oldest = freshSessions[0]; // sorted by created_at ASC
        const kicked = await SessionService.forceCloseSession(oldest.id, userId);
        if (kicked) {
          return { allowed: true, kickedDeviceName: oldest.deviceName };
        }
        // If kick failed, still allow (fail open)
        logger.error("[SessionService] Auto-kick failed, proceeding anyway");
      }

      return { allowed: true };
    } catch (err) {
      // Fail open — if we can't check, let the user through
      logger.error("[SessionService] checkSessionAccess failed:", err);
      return { allowed: true };
    }
  }

  /**
   * Get only fresh active sessions (heartbeat within threshold).
   * Sorted by created_at ASC (oldest first) for auto-kick logic.
   */
  static async getFreshActiveSessions(userId: string): Promise<ActiveSession[]> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase.rpc("get_fresh_active_sessions", {
        p_user_id: userId,
        p_max_age_seconds: GHOST_THRESHOLD_SECONDS,
      });
      if (error) {
        logger.error("[SessionService] getFreshActiveSessions failed:", error);
        return [];
      }
      return (data ?? []).map((row: Record<string, unknown>) => mapSession(row));
    } catch (err) {
      logger.error("[SessionService] getFreshActiveSessions exception:", err);
      return [];
    }
  }

  /**
   * Terminate ghost sessions for a user (heartbeat older than 90s).
   * Called before session count check to free up stale slots immediately.
   */
  static async cleanupGhostSessions(userId: string): Promise<number> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase.rpc("cleanup_ghost_sessions", {
        p_user_id: userId,
        p_max_age_seconds: GHOST_THRESHOLD_SECONDS,
      });
      if (error) {
        logger.error("[SessionService] cleanupGhostSessions failed:", error);
        return 0;
      }
      return (data as number) ?? 0;
    } catch (err) {
      logger.error("[SessionService] cleanupGhostSessions exception:", err);
      return 0;
    }
  }

  /**
   * Create a new active session record.
   */
  static async createSession(
    userId: string,
    tenantId: string,
    licenseType: string,
    deviceInfo: DeviceInfo
  ): Promise<CreateSessionResult> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("active_sessions")
        .insert({
          user_id: userId,
          tenant_id: tenantId,
          license_type: licenseType,
          device_type: deviceInfo.device_type,
          client_type: deviceInfo.client_type,
          device_fingerprint: deviceInfo.device_fingerprint,
          device_name: deviceInfo.device_name,
        })
        .select("id")
        .single();

      if (error) {
        logger.error("[SessionService] createSession failed:", error);
        return { sessionId: "", success: false };
      }
      return { sessionId: data.id, success: true };
    } catch (err) {
      logger.error("[SessionService] createSession exception:", err);
      return { sessionId: "", success: false };
    }
  }

  /**
   * Force-close a session (used for auto-kick and logout).
   */
  static async forceCloseSession(sessionId: string, userId: string): Promise<boolean> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase.rpc("terminate_session", {
        p_session_id: sessionId,
        p_user_id: userId,
        p_reason: "kicked",
      });
      if (error) {
        logger.error("[SessionService] forceCloseSession failed:", error);
        return false;
      }
      return data === true;
    } catch (err) {
      logger.error("[SessionService] forceCloseSession exception:", err);
      return false;
    }
  }

  /**
   * Get the status and termination reason for a session.
   * Used to distinguish kicked sessions (should block) from timed-out ones (allow re-login).
   * Returns null if the session row doesn't exist (deleted by cleanup).
   */
  static async getSessionStatus(sessionId: string): Promise<{ status: string; terminatedReason: string | null } | null> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("active_sessions")
        .select("status, terminated_reason")
        .eq("id", sessionId)
        .single();
      if (error || !data) return null;
      return { status: data.status as string, terminatedReason: data.terminated_reason as string | null };
    } catch {
      return null;
    }
  }

  /**
   * Send a heartbeat to keep the session alive.
   */
  static async sendHeartbeat(sessionId: string): Promise<boolean> {
    try {
      const supabase = requireSupabase();
      const { error } = await supabase
        .from("active_sessions")
        .update({ last_heartbeat: new Date().toISOString() })
        .eq("id", sessionId)
        .eq("status", "active");
      if (error) {
        logger.error("[SessionService] sendHeartbeat failed:", error);
        return false;
      }
      return true;
    } catch (err) {
      logger.error("[SessionService] sendHeartbeat exception:", err);
      return false;
    }
  }

  /**
   * Check whether a session is still valid (active status, not terminated).
   */
  static async isSessionValid(sessionId: string): Promise<boolean> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("active_sessions")
        .select("status")
        .eq("id", sessionId)
        .single();
      if (error || !data) return false;
      return data.status === "active";
    } catch {
      return false;
    }
  }

  /**
   * Terminate the current session (used during logout).
   */
  static async terminateCurrentSession(sessionId: string, userId: string): Promise<void> {
    try {
      const supabase = requireSupabase();
      await supabase.rpc("terminate_session", {
        p_session_id: sessionId,
        p_user_id: userId,
        p_reason: "user_logout",
      });
    } catch (err) {
      logger.error("[SessionService] terminateCurrentSession failed:", err);
    }
  }
}
